// SignDocumentButton Component
// Displays "Sign Now" or "Verify & Sign" button based on authentication and tenant config

import React, { useState } from 'react'
import { Zap, Lock, Loader2 } from 'lucide-react'
import type { SignDocument } from '@/types/sign'

export interface SignDocumentButtonProps {
  document: SignDocument
  currentUserId?: string
  isAuthenticated: boolean
  instantSigningEnabled: boolean
  onSignClick: (documentId: string, signerId: string) => Promise<void>
  className?: string
}

export const SignDocumentButton: React.FC<SignDocumentButtonProps> = ({
  document,
  currentUserId,
  isAuthenticated,
  instantSigningEnabled,
  onSignClick,
  className = ''
}) => {
  const [isLoading, setIsLoading] = useState(false)

  // Find the current user's signer record
  const currentUserSigner = document.signers.find(
    (signer) => signer.userId === currentUserId || signer.email === currentUserId
  )

  // Don't show button if user is not a signer or already signed
  if (
    !currentUserSigner ||
    currentUserSigner.status === 'completed' ||
    currentUserSigner.status === 'declined'
  ) {
    return null
  }

  // Determine if instant signing should be shown
  const shouldShowInstantSigning = isAuthenticated && instantSigningEnabled
  const buttonText = shouldShowInstantSigning ? '⚡ Sign Now' : '🔐 Verify & Sign'
  const Icon = shouldShowInstantSigning ? Zap : Lock

  const handleClick = async () => {
    if (isLoading || !currentUserSigner) return

    setIsLoading(true)
    try {
      await onSignClick(document.documentId, currentUserSigner.signerId)
    } catch (error) {
      console.error('Error initiating sign flow:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`
        relative group
        inline-flex items-center justify-center gap-2
        px-4 py-2 min-h-[44px] min-w-[44px]
        font-semibold text-white
        bg-gradient-to-r from-blue-500 to-purple-600
        hover:from-blue-600 hover:to-purple-700
        active:from-blue-700 active:to-purple-800
        rounded-lg
        transition-all duration-200
        focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-brand-dark
        disabled:opacity-50 disabled:cursor-not-allowed
        ${shouldShowInstantSigning ? 'animate-subtle-pulse' : ''}
        ${className}
      `}
      aria-label={
        shouldShowInstantSigning
          ? 'Sign document instantly without OTP'
          : 'Sign document with verification'
      }
      title={shouldShowInstantSigning ? '⚡ Instant' : 'Requires verification'}
    >
      {isLoading ? (
        <>
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading...</span>
        </>
      ) : (
        <>
          <Icon className="w-5 h-5" />
          <span>{buttonText}</span>
        </>
      )}

      {/* Tooltip */}
      {shouldShowInstantSigning && !isLoading && (
        <span
          className="
            absolute -top-10 left-1/2 -translate-x-1/2
            px-3 py-1 rounded-md
            bg-brand-dark-lighter border border-brand-primary/30
            text-xs font-medium text-brand-primary
            opacity-0 group-hover:opacity-100
            transition-opacity duration-200
            pointer-events-none
            whitespace-nowrap
          "
        >
          ⚡ Instant
        </span>
      )}
    </button>
  )
}
